import React, { useState } from 'react';
import {
  CreditCard,
  Lock,
  Check,
  Loader,
  AlertCircle,
  Shield,
  Calendar,
  DollarSign
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { paymentService } from '../services/payment';
import { SUBSCRIPTION_PLANS } from '../config/stripe';
import toast from 'react-hot-toast';

interface CheckoutFormProps {
  planType: 'pro_monthly' | 'pro_yearly';
  onSuccess?: () => void;
  onCancel?: () => void;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({ 
  planType, 
  onSuccess, 
  onCancel 
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState(planType);

  const plan = SUBSCRIPTION_PLANS[selectedPlan];

  const handleCheckout = async () => {
    if (!user) {
      setError('Please sign in to subscribe');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Process payment with Stripe
      await paymentService.processSubscription(user, selectedPlan);
      
      // Show success message
      toast.success('Redirecting to checkout...');
      
      // Call success callback
      if (onSuccess) {
        setTimeout(onSuccess, 2000);
      }
    } catch (err: any) {
      setError(err.message || 'Payment failed. Please try again.');
      toast.error('Payment failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      {/* Plan Selection */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
          Select Your Plan
        </h3>
        
        <div className="space-y-3">
          {/* Monthly Plan */}
          <label className="relative block cursor-pointer">
            <input
              type="radio"
              name="plan"
              value="pro_monthly"
              checked={selectedPlan === 'pro_monthly'}
              onChange={(e) => setSelectedPlan(e.target.value as 'pro_monthly')}
              className="sr-only"
            />
            <div className={`border-2 rounded-lg p-4 transition-all ${
              selectedPlan === 'pro_monthly'
                ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-amber-600" />
                    <span className="font-semibold text-gray-900 dark:text-white">
                      Monthly Plan
                    </span>
                    {SUBSCRIPTION_PLANS.pro_monthly.popular && (
                      <span className="px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">
                        POPULAR
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Billed monthly, cancel anytime
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    ${SUBSCRIPTION_PLANS.pro_monthly.price}
                  </p>
                  <p className="text-xs text-gray-500">/month</p>
                </div>
              </div>
            </div>
          </label>

          {/* Yearly Plan */}
          <label className="relative block cursor-pointer">
            <input
              type="radio"
              name="plan"
              value="pro_yearly"
              checked={selectedPlan === 'pro_yearly'}
              onChange={(e) => setSelectedPlan(e.target.value as 'pro_yearly')}
              className="sr-only"
            />
            <div className={`border-2 rounded-lg p-4 transition-all ${
              selectedPlan === 'pro_yearly'
                ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-600" />
                    <span className="font-semibold text-gray-900 dark:text-white">
                      Yearly Plan
                    </span>
                    {SUBSCRIPTION_PLANS.pro_yearly.savings && (
                      <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">
                        SAVE 17%
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {SUBSCRIPTION_PLANS.pro_yearly.savings} • Billed annually
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    ${SUBSCRIPTION_PLANS.pro_yearly.price}
                  </p>
                  <p className="text-xs text-gray-500">/year</p>
                </div>
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Features List */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-3">
          What's Included:
        </h4>
        <ul className="space-y-2">
          {plan.features.slice(0, 5).map((feature, index) => (
            <li key={index} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Security Badge */}
      <div className="flex items-center justify-center gap-2 mb-6 text-sm text-gray-600 dark:text-gray-400">
        <Shield className="w-4 h-4 text-green-500" />
        <span>Secure payment powered by Stripe</span>
        <Lock className="w-4 h-4" />
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
            <AlertCircle className="w-5 h-5" />
            <span className="text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          onClick={handleCheckout}
          disabled={loading}
          className="flex-1 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5" />
              Subscribe Now
            </>
          )}
        </button>
        
        {onCancel && (
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-all disabled:opacity-50"
          >
            Cancel
          </button>
        )}
      </div>

      {/* Terms */}
      <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-4">
        By subscribing, you agree to our{' '}
        <a href="/terms" className="text-amber-600 hover:underline">
          Terms of Service
        </a>{' '}
        and{' '}
        <a href="/privacy" className="text-amber-600 hover:underline">
          Privacy Policy
        </a>
      </p>
    </div>
  );
};

export default CheckoutForm;